import { appConfig } from 'config'
import Cookies from 'js-cookie'
import { useEffect, useState } from 'react'

export interface User {
  id: string
  email: string
  emailVerified?: boolean
  name: string
  image?: string
  businessLineId?: string
  userRoles?: { name: string; permissions: string[] }[]
  userApplications?: string[]
  status?: string
  createdAt?: string
  updatedAt?: string
  organizationId?: string
}

export interface ExternalAuthConfig {
  authServerUrl: string
  currentAppName: string
}

export interface ExternalAuthReturn {
  user: User | null
  isLoading: boolean
  login: () => void
  logout: () => void
  isAuthenticated: boolean
}

export const useExternalAuth = (config: ExternalAuthConfig): ExternalAuthReturn => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Verificar si hay token guardado
    setIsLoading(false)
    const accessToken = Cookies.get('access-token')
    const refreshToken = Cookies.get('refresh-token')
    const userData = Cookies.get('user-data')

    if (accessToken && userData && refreshToken) {
      setUser(JSON.parse(userData))
    }
  }, [])

  function authenticateWithPopup(
    authServerUrl: string,
    currentAppName: string
  ): Promise<{ accessToken: string; refreshToken: string; user: User }> {
    return new Promise((resolve, reject) => {
      let popup: Window | null

      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768

      if (isMobile) {
        // En móvil: abrir en la misma pestaña o nueva pestaña
        popup = window.open(
          `${authServerUrl}/auth/signin?appName=${encodeURIComponent(currentAppName)}&redirectUrl=${encodeURIComponent(
            window.location.origin
          )}&mode=popup&l=true`,
          '_blank' // o '_self' para misma pestaña
        )
      } else {
        // En desktop: popup centrado
        const popupWidth = 500
        const popupHeight = 600
        const left = (window.screen.width - popupWidth) / 2
        const top = (window.screen.height - popupHeight) / 2

        popup = window.open(
          `${authServerUrl}/auth/signin?appName=${encodeURIComponent(currentAppName)}&redirectUrl=${encodeURIComponent(
            window.location.origin
          )}&mode=popup&l=true`,
          'auth_popup',
          `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes`
        )
      }

      interface AuthMessageData {
        success: boolean
        accessToken?: string
        refreshToken?: string
        user?: User
        error?: string
      }

      interface AuthMessageEvent extends MessageEvent {
        data: AuthMessageData
      }

      function handleMessage(event: AuthMessageEvent) {
        // Verificar origen por seguridad
        if (event.origin !== new URL(authServerUrl).origin) {
          return
        }

        window.removeEventListener('message', handleMessage)

        if (event.data.success) {
          resolve({
            accessToken: event.data.accessToken!,
            refreshToken: event.data.refreshToken!,
            user: event.data.user!
          })
        } else {
          reject(new Error(event.data.error || 'Authentication failed'))
        }
      }

      window.addEventListener('message', handleMessage)

      // Manejar si el usuario cierra el popup
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed)
          window.removeEventListener('message', handleMessage)
          console.error('Authentication cancelled')
        }
      }, 1000)
    })
  }

  const login = async (method = 'popup') => {
    const authServerUrl = config.authServerUrl
    const currentAppName = config.currentAppName

    try {
      if (method === 'popup') {
        const result = await authenticateWithPopup(authServerUrl, currentAppName)
        Cookies.set('access-token', result.accessToken, {
          expires: 7,
          secure: true,
          sameSite: 'Strict'
        })
        Cookies.set('refresh-token', result.refreshToken, {
          expires: 40,
          secure: true,
          sameSite: 'Strict'
        })
        Cookies.set('user-data', JSON.stringify(result.user), {
          expires: 7,
          secure: true,
          sameSite: 'Strict'
        })

        try {
          const imageResponse = await fetch(`${authServerUrl}/api/users/image`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${result.accessToken}`
            }
          })

          if (imageResponse.ok) {
            const { image } = await imageResponse.json()
            setUser({ ...result.user, image })
          } else {
            console.error('Error fetching user image:', imageResponse.statusText)
            setUser(result.user)
          }
        } catch (error) {
          console.error('Error fetching user image:', error)
          setUser(result.user)
        }

        // get company id from plan vital
        const companyID = await getPlanVitalCompanyID(result.user.businessLineId || '', result.accessToken)

        Cookies.set('company-id', companyID ?? '', {
          expires: 7,
          secure: true,
          sameSite: 'Strict'
        })
      }
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  const logout = () => {
    Cookies.remove('access-token')
    Cookies.remove('refresh-token')
    Cookies.remove('user-data')
    Cookies.remove('company-id')
    setUser(null)
    window.location.href = '/'
  }

  return {
    user,
    isLoading,
    login,
    logout,
    isAuthenticated: !!user
  }
}

const getPlanVitalCompanyID = async (serviceLineId: string, accessToken: string): Promise<string | null> => {
  try {
    const response = await fetch(`${appConfig.apiUrlExternal}/utils/company-by-service-line?service_line_id=${serviceLineId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`
      }
    })

    if (!response.ok) {
      console.error('Error fetching company ID:', response.statusText)
      return null
    }

    const { data } = await response.json()

    if (!data || data.length === 0) {
      console.error('No companys found for the given service line ID')
      return null
    }

    // buscar el company con nombre 'Plan Vital'
    const planVitalCompany = data.find((company: any) => company.razon_social === 'Plan Vital')

    if (!planVitalCompany) {
      console.error("Company with name 'Plan Vital' not found")
      return null
    }

    return planVitalCompany.uuid
  } catch (error) {
    console.error('Error fetching company ID:', error)
    return null
  }
}
